import keycloak from '../keycloak';
import { getStorage, setStorage, type StoredTokenObject } from './authStorage';
import { isStoredTokenValid } from './authSession';
import { getJwtExpSeconds } from './jwt';

/** Minimum validity (seconds) before Keycloak refresh on API calls. */
export const TOKEN_MIN_VALIDITY_SEC = 30;

/** Refresh proactively this many seconds before access-token expiry. */
export const TOKEN_REFRESH_AHEAD_SEC = 90;

export function buildTokenObject(): StoredTokenObject | null {
  if (!keycloak.authenticated || !keycloak.token) return null;
  const exp = keycloak.tokenParsed?.exp ?? getJwtExpSeconds(keycloak.token);
  const now = Math.floor(Date.now() / 1000);
  const expiresIn = exp != null ? Math.max(0, exp - now) : 3600;
  const refreshExp = getJwtExpSeconds(keycloak.refreshToken);
  return {
    access_token: keycloak.token,
    id_token: keycloak.idToken ?? undefined,
    refresh_token: keycloak.refreshToken ?? undefined,
    type: 'Bearer',
    expires_in: expiresIn,
    token_expires_at: exp ?? 0,
  };
}

export function persistKeycloakAuthState(): void {
  if (!keycloak.authenticated || !keycloak.token) return;
  if (getStorage<boolean>('timeOut') === true) return;
  const tokenObject = buildTokenObject();
  if (!tokenObject) return;
  const stored = getStorage<StoredTokenObject>('token');
  if (
    stored?.access_token === tokenObject.access_token &&
    stored.token_expires_at === tokenObject.token_expires_at
  ) {
    return;
  }
  setStorage('token', tokenObject);
}

/**
 * Returns a valid access token, preferring the live Keycloak session with refresh.
 * Never returns an expired cached token.
 */
export async function getAccessToken(): Promise<string | undefined> {
  if (getStorage<boolean>('timeOut') === true) return undefined;

  if (keycloak.authenticated) {
    try {
      await keycloak.updateToken(TOKEN_MIN_VALIDITY_SEC);
      if (keycloak.token) {
        persistKeycloakAuthState();
        return keycloak.token;
      }
    } catch {
      if (keycloak.token) {
        const exp = keycloak.tokenParsed?.exp ?? getJwtExpSeconds(keycloak.token);
        const now = Math.floor(Date.now() / 1000);
        if (exp != null && exp > now) return keycloak.token;
      }
    }
  }

  const stored = getStorage<StoredTokenObject>('token');
  if (stored && isStoredTokenValid(stored, 0)) {
    return stored.access_token;
  }

  return undefined;
}

let refreshInFlight: Promise<string | null> | null = null;

/** Force-refresh the access token; returns null when refresh is not possible. */
export async function refreshAccessToken(): Promise<string | null> {
  if (getStorage<boolean>('timeOut') === true) return null;
  if (!keycloak.authenticated) return null;

  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    try {
      await keycloak.updateToken(0);
      if (keycloak.token) {
        persistKeycloakAuthState();
        return keycloak.token;
      }
      return null;
    } catch {
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

export function getLiveAccessTokenExpirySec(): number | null {
  const accessExp = keycloak.tokenParsed?.exp ?? getJwtExpSeconds(keycloak.token);
  if (typeof accessExp === 'number' && Number.isFinite(accessExp)) return accessExp;

  const stored = getStorage<StoredTokenObject>('token');
  if (stored?.token_expires_at) return stored.token_expires_at;

  return null;
}
